'use client'

import Image from 'next/image'
import { ButtonOrLink } from '@/components/ButtonOrLink'
import { Sticker } from '@c/Sticker'
import { Arrow3Icon } from '@/components/icon/icons/Arrow3Icon'
import s from './ArchiveItemPreview.module.scss'

import Link from 'next/link'
import { useState, useMemo } from 'react'
import {
  ArchiveItemPreviewProps,
  NormalizedArchiveData,
} from './ArchiveItemPreview.types'
import {
  ArchiveData,
  ArchiveInterface,
  ArchiveWithVariants,
} from '@/structures/interfaces/ArchiveInterface'
import {
  TRAFFIC_SIGN_CATEGORY_TILE_BG_PNG,
  TRAFFIC_SIGN_CATEGORY_TILE_BG_PNG_DARK,
  isTrafficSignCategoryTileImage,
} from '@/constants/trafficSignCategoryTile'
import { useDataTheme } from '@/hooks/useZebraLogoSrc'

// Funkcja pomocnicza do normalizacji danych
function normalizeData(data: ArchiveData): NormalizedArchiveData {
  // Sprawdzamy czy to tablica
  if (Array.isArray(data)) {
    return {
      items: data,
      hasVariants: data.length > 1,
    }
  }

  // Sprawdzamy czy to obiekt z wariantami
  if ('variants' in data && data.variants) {
    const { variants } = data as ArchiveWithVariants
    // Wyświetlamy TYLKO warianty, nie główny obiekt
    // Warianty już mają wszystkie dane z baseArchive + nadpisane wartości
    return {
      items: variants,
      hasVariants: variants.length > 1,
    }
  }

  // To pojedynczy obiekt
  return {
    items: [data as ArchiveInterface],
    hasVariants: false,
  }
}

export function ArchiveItemPreview({
  data,
  dict,
  urlBase,
}: ArchiveItemPreviewProps) {
  const normalizedData = useMemo(() => normalizeData(data), [data])

  const [currentIndex, setCurrentIndex] = useState(0)

  const currentItem = normalizedData.items[currentIndex]
  const { slug, category, title, excerpt, tags, image, overlayImage } =
    currentItem

  const link = category.slug
    ? `/${urlBase}/${category.slug}/${slug}`
    : `/${urlBase}/${slug}`

  const isTrafficSign = urlBase === 'znaki-drogowe'
  const isTrafficSignCategory = isTrafficSign && !category.slug
  const isActualTrafficSign = isTrafficSign && category.slug

  const handlePrevious = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? normalizedData.items.length - 1 : prev - 1,
    )
  }

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === normalizedData.items.length - 1 ? 0 : prev + 1,
    )
  }

  const useCategoryPngTile =
    isTrafficSignCategory && isTrafficSignCategoryTileImage(image)

  const theme = useDataTheme()
  const categoryTileBgSrc =
    theme === 'dark'
      ? TRAFFIC_SIGN_CATEGORY_TILE_BG_PNG_DARK
      : TRAFFIC_SIGN_CATEGORY_TILE_BG_PNG

  const renderImage = () => {
    const imageContent = (
      <>
        {!image || image === 'no-svg' ? (
          <div className={s.noSvgPlaceholder}>Brak SVG</div>
        ) : useCategoryPngTile ? (
          <div className={s.patternBgWrapper}>
            {/* Warstwa 1: raster PNG (4096² — cover), nie pattern SVG */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={categoryTileBgSrc}
              alt=""
              className={s.categoryTilePngBgImg}
              aria-hidden
              draggable={false}
            />
            {overlayImage && (
              <div className={s.patternOverlay}>
                {/* Warstwa 2: znak drogowy (SVG z API) */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={overlayImage}
                  alt=""
                  className={s.patternOverlayImg}
                />
              </div>
            )}
          </div>
        ) : image.endsWith('.svg') ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={image} alt={title} className={s.svgImage} />
        ) : (
          <Image
            src={image}
            alt={title}
            fill
            loading="lazy"
            style={{ objectFit: 'cover' }}
          />
        )}
        <div className={s.tags}>
          {tags.slice(0, 3).map((tag) => (
            <Sticker key={tag} className={s.tag} trafficSign={!!isTrafficSign}>
              {tag}
            </Sticker>
          ))}
        </div>
      </>
    )

    const wrapperClasses = `${s.imageWrapper} ${
      useCategoryPngTile
        ? s.imageMode
        : image === 'no-svg' || image.endsWith('.svg')
          ? s.svgMode
          : s.imageMode
    }`

    if (isTrafficSign && !isTrafficSignCategory) {
      return (
        <div className={`${wrapperClasses} ${s.nonClickable}`}>
          {imageContent}
        </div>
      )
    }

    if (normalizedData.hasVariants) {
      return <div className={wrapperClasses}>{imageContent}</div>
    }

    return (
      <Link href={link}>
        <div className={wrapperClasses}>{imageContent}</div>
      </Link>
    )
  }

  const renderVariantNavigation = () => {
    if (!normalizedData.hasVariants) return null

    return (
      <div className={s.variantNavigation}>
        <button
          className={`${s.navButton} ${s.navButtonPrev}`}
          onClick={handlePrevious}
          aria-label="Poprzedni wariant"
        >
          <Arrow3Icon />
        </button>
        <span className={s.variantCounter}>
          wariant {currentIndex + 1} z {normalizedData.items.length}
        </span>
        <button
          className={`${s.navButton} ${s.navButtonNext}`}
          onClick={handleNext}
          aria-label="Następny wariant"
        >
          <Arrow3Icon />
        </button>
      </div>
    )
  }

  const trafficSignAnchorId =
    isActualTrafficSign && slug ? `traffic-sign-${slug}` : undefined

  return (
    <article className={s.card} id={trafficSignAnchorId}>
      <div className={s.imageAndNavWrapper}>
        {renderImage()}
        {renderVariantNavigation()}
      </div>

      <div className={s.content}>
        <h3 className={s.title}>{title}</h3>
        <p className={s.excerpt}>{excerpt}</p>
        {!isActualTrafficSign && (
          <ButtonOrLink
            as="link"
            href={link}
            variant="text"
            iconRight="Arrow3Icon"
            classes={s.readMore}
          >
            {isTrafficSignCategory
              ? 'Zobacz wszystkie'
              : (dict?.readmore as string) || 'Czytaj więcej'}
          </ButtonOrLink>
        )}
      </div>
    </article>
  )
}
